import { useSelector, useDispatch } from "react-redux";
import trackingConsole from '../../../utils/trackingConsole';
import { createSelector } from "reselect";
import CoveredEntity from "../../../models/CoveredEntity";
import { fetchRecordsSuccess } from "../../utility";
import { isListEmpty, isListNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import ProductCategory from "../../../models/ProductCategory";
import Product from "../../../models/Product";
import { getAffiliateById, getAgreementByKey, getArticleById, getArticleBySlug, getArticleCategoryByArticleId, getArticleCategoryById, getArticleCategoryBySlug, getArticlesByCategoryId, getArticlesByCategorySlug, getCampaignById, getCampaignByProgramSlug, getCampaignBySlug, getChildArticleCategoriesByParentId, getChildProductCategoriesByParentId, getCommercialEquivalencyGroupById, getConditionInfo, getCoveredEntityBySlug, getCoveredEntityInfo, getDefaultCampaign, getDefaultProduct, getMetricInfo, getProductByShortSku, getProductBySku, getProductBySlug, getProductCategoriesByProductId, getProductCategoriesBySlugs, getProductCategoryById, getProductCategoryBySlug, getProductFamilyById, getProductsByCategoryId, getProductsByCategorySlug, getProductsByProgramId, getProductsByProgramSlug, getProgramById, getProgramBySlug, getProviderByUserId, getOfferGroupById, getOfferGroupByToken, getFunnelChoiceSetById, getSurveyBySlug, getSurveyInfo, getSurveysByCampaignId, getSurveysByCampaignSlug, getSurveysByProductSku, getSurveysByProgramId, getSurveysByProgramSlug, getTestInfo, getVaccinationInfo, getVariantById, getVariantBySku, getVariantBySlug, getVariantsByProductId, getVariantsByProductSku, getVariantsByProductSlug, getEventById, getPartnerById } from "../../../utils/entityUtils";
import Agreement from "../../../models/Agreement";
import Survey from "../../../models/Survey";
import logger from "../../../utils/logger";
import Condition from "../../../models/Condition";
import { getErrorMessage } from "../../../utils/errorUtils";
import Biomarker from "../../../models/Biomarker";
import ShippingClass from "../../../models/ShippingClass";
import ShippingMethod from "../../../models/ShippingMethod";
import Campaign from "../../../models/Campaign";
import Affiliate from "../../../models/Affiliate";
import Program from "../../../models/Program";
import ProductFamily from "../../../models/ProductFamily";
import ProductVariant from "../../../models/ProductVariant";
import Provider from "../../../models/Provider";
import OfferGroup from "../../../models/OfferGroup";
import CommercialEquivalencyGroup from "../../../models/CommercialEquivalencyGroup";
import FunnelChoiceSet from "../../../models/FunnelChoiceSet";
import Article from "../../../models/Article";
import ArticleCategory from "../../../models/ArticleCategory";
import Event from "../../../models/Event";
import moment from "moment-timezone";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { clearCache, readCache, writeCache } from "../../../utils/indexedDbUtils";
import Partner from "../../../models/Partner";
import { orderBy, toLower } from "lodash";

//#region Action Types

const FETCH_ORCHESTRATOR_SUCCESS = "FETCH_ORCHESTRATOR_SUCCESS";

//#endregion
//#region Actions

const getOrchestratorSuccess = (payload) => { return { type: FETCH_ORCHESTRATOR_SUCCESS, payload: payload } };

export const getOrchestrator = (force = false) => {
    return async (dispatch, getState) => {
        try {
            await Promise.all([
                dispatch(getPrograms(force)),
                dispatch(getProducts(force)),
                dispatch(getVariants(force)),
                dispatch(getProductCategories(force)),
                dispatch(getCoveredEntities(force)),
                dispatch(getProviders(force)),
                dispatch(getAgreements(force)),
                dispatch(getSurveys(force)),
                // dispatch(getConditions(force)),
                // dispatch(getTests(force)),
                // dispatch(getVaccinations(force)),
                // dispatch(getMetrics(force)),
                dispatch(getShippingClasses(force)),
                dispatch(getShippingMethods(force)),
                dispatch(getCampaigns(force)),
                dispatch(getProductFamilies(force)),
                dispatch(getOfferGroups(force)),
                dispatch(getCommercialEquivalencyGroups(force)),
                dispatch(getFunnelChoiceSets(force)),
                // dispatch(getAffiliates(force)),
                // dispatch(getPartners(force)),
                // dispatch(getArticles(force)),
                // dispatch(getArticleCategories(force)),
                // events are loaded on demand by the admin event modal; nothing reads
                // state.orchestrator.events, so a boot-time paginated query only cost a render
                // dispatch(getEventsInRange(force)),
            ]);

            // the getters above hand a bundle's fresh-stamped partial lists straight back, so FULL waits until
            // refreshBundleScopedCollections has replaced them - stamping it now would stop /e asking for its bundle
            if (force === true || getState()?.orchestrator?.bootstrapScope !== 'FUNNEL_BUNDLE') {
                dispatch(getOrchestratorSuccess({ bootstrapScope: 'FULL' }));
            }

            return true;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return false;
    }
};

// only defaulted for production builds - a dev/staging project without an override degrades to
// the collection-getter path instead of reading prod bundles
const FUNNEL_BUNDLE_API_URL = isStringNotEmpty(process.env.REACT_APP_FUNNEL_BUNDLE_API_URL)
    ? process.env.REACT_APP_FUNNEL_BUNDLE_API_URL
    : (process.env.REACT_APP_FIREBASE_PROJECT_ID === 'ready-rx-cd342' ? 'https://getfunnelbootstrapapi-vxwmzv7rgq-uc.a.run.app' : null);

const FUNNEL_BUNDLE_STORAGE_PREFIX = 'funnelBundle:';
// the encounter funnel knows its program by id, but the bundle cache is keyed by slug. Every bundle
// ships the full programs list, so the map is free to keep here - and cheap to read, where parsing a
// whole cached bundle just to recover one slug would cost more than the lookup saves.
const FUNNEL_BUNDLE_PROGRAM_INDEX_KEY = 'funnelBundleProgramIndex';
// scoped bundles multiply the cache entries (per program, per intake/consult/followup), so the cap
// covers a couple of programs across the funnel and encounter stages rather than one program's funnel
const FUNNEL_BUNDLE_MAX_STORED = 6;
const FUNNEL_BUNDLE_FRESH_MS = 60 * 60 * 1000;
const FUNNEL_BUNDLE_COLLECTIONS = ['programs', 'productFamilies', 'offerGroups', 'funnelChoiceSets', 'shippingClasses', 'shippingMethods', 'campaigns', 'coveredEntities', 'products', 'variants', 'surveys'];
// the bundle trims these three to the program and ships the rest whole (functions/src/funnelBootstrap.js)
const BUNDLE_SCOPED_COLLECTIONS = ['products', 'variants', 'surveys'];

// must require everything initiateFunnel gates on (programs, products, variants, surveys) - a
// bundle missing any of them has to fall back to the collection getters rather than be applied,
// or the funnel silently never initializes
const isFunnelBundleUsable = (bundle) => {
    const collections = bundle?.collections;
    return isObjectNotEmpty(collections)
        && isListNotEmpty(collections.programs)
        && isListNotEmpty(collections.products)
        && isListNotEmpty(collections.variants)
        && isListNotEmpty(collections.surveys);
};

// the bundle carries the orchestratorVersions stamp of every collection it ships, so an identical
// stamp map means an identical payload - re-applying it would replace each collection's identity
// and re-render every consumer for nothing
const hasSameBundleVersions = (storedBundle, freshBundle) => {
    const storedVersions = storedBundle?.versions ?? {};
    const freshVersions = freshBundle?.versions ?? {};
    const versionKeys = new Set([...Object.keys(storedVersions), ...Object.keys(freshVersions)]);

    return [...versionKeys].every((versionKey) => storedVersions[versionKey] === freshVersions[versionKey]);
};

// dispatches a bundle into the same state keys the collection getters use, stamped fresh so the
// initiateFunnel data guard early-returns; the caches on disk are NOT written (the bundle's big
// collections are partial and must never masquerade as full cached copies)
const applyFunnelBundle = (dispatch, bundle) => {
    const now = new Date();
    const payload = {};
    FUNNEL_BUNDLE_COLLECTIONS.forEach((listKey) => {
        const list = bundle.collections?.[listKey];
        if (Array.isArray(list)) {
            payload[listKey] = listKey === 'products' ? orderBy(list, ['name'], ['asc']) : list;
            payload[listKey + 'LastUpdatedAt'] = now;
            payload[listKey + 'Version'] = bundle.versions?.[listKey] ?? null;
        }
    });
    dispatch(getOrchestratorSuccess(payload));
};

// keyed by program AND scope so the funnel's intake bundle and the encounter's consult/followup
// bundle for the same program coexist in the cache instead of evicting each other
const getFunnelBundleStorageKey = (programSlug, scope) => `${FUNNEL_BUNDLE_STORAGE_PREFIX}${programSlug}:${scope}`;

const storeFunnelBundle = (programSlug, bundle, scope) => {
    setTimeout(() => {
        try {
            localStorage.setItem(getFunnelBundleStorageKey(programSlug, scope), JSON.stringify({ storedAt: Date.now(), bundle: bundle }));

            const programIndex = {};
            (bundle?.collections?.programs ?? []).forEach((program) => {
                if (isStringNotEmpty(program?.id) && isStringNotEmpty(program?.slug)) {
                    programIndex[program.id] = toLower(program.slug);
                }
            });
            if (isObjectNotEmpty(programIndex)) {
                localStorage.setItem(FUNNEL_BUNDLE_PROGRAM_INDEX_KEY, JSON.stringify(programIndex));
            }

            // keep only the most recent program bundles so the origin's quota stays comfortable
            const bundleKeys = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key.startsWith(FUNNEL_BUNDLE_STORAGE_PREFIX)) {
                    let storedAt = 0;
                    try { storedAt = JSON.parse(localStorage.getItem(key))?.storedAt ?? 0; } catch (parseError) { storedAt = 0; }
                    bundleKeys.push({ key, storedAt });
                }
            }
            bundleKeys.sort((a, b) => b.storedAt - a.storedAt);
            bundleKeys.slice(FUNNEL_BUNDLE_MAX_STORED).forEach((entry) => { localStorage.removeItem(entry.key); });
        } catch (cacheError) {
            trackingConsole.warn('Failed to persist funnel bundle:', cacheError);
        }
    }, 0);
};

const readStoredFunnelBundle = (programSlug, scope) => {
    try {
        const stored = localStorage.getItem(getFunnelBundleStorageKey(programSlug, scope));
        if (isStringNotEmpty(stored)) {
            const parsed = JSON.parse(stored);
            if (isFunnelBundleUsable(parsed?.bundle)) {
                return parsed;
            }
        }
    } catch (cacheError) {
        // fall through to the network path
    }

    return null;
};

const getProgramSlugFromIndex = (programId) => {
    if (isStringEmpty(programId)) {
        return null;
    }

    try {
        const stored = localStorage.getItem(FUNNEL_BUNDLE_PROGRAM_INDEX_KEY);
        if (isStringNotEmpty(stored)) {
            return JSON.parse(stored)?.[programId] ?? null;
        }
    } catch (indexError) {
        // fall through; the endpoint resolves the id itself
    }

    return null;
};

// /f holds its first paint for the intake bundle; /e has painted already and its fallback is the ~10MB
// catalog load, which even a slow bundle beats - so the encounter waits longer, inside the endpoint's 30s
const FUNNEL_BUNDLE_TIMEOUT_MS = { intake: 6000, consult: 20000, followup: 20000 };

// the slug identifies a bundle when it is in the url, the id when it was read off an encounter; the scope
// selects the survey stage (intake for the funnel, consult/followup for the encounter). Every failure
// throws, so an error status or an unusable body is reported the same way a timeout is
const fetchFunnelBundle = async (programSlug, programId, scope) => {
    const identifier = isStringNotEmpty(programSlug)
        ? `program=${encodeURIComponent(programSlug)}`
        : `programId=${encodeURIComponent(programId)}`;
    const query = `${identifier}&scope=${encodeURIComponent(scope)}`;
    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), FUNNEL_BUNDLE_TIMEOUT_MS[scope] ?? FUNNEL_BUNDLE_TIMEOUT_MS.intake);
    try {
        const bundleResponse = await fetch(`${FUNNEL_BUNDLE_API_URL}?${query}`, { signal: abortController.signal });
        if (bundleResponse.ok === false) {
            throw new Error(`Funnel bundle request returned ${bundleResponse.status}`);
        }

        const bundle = await bundleResponse.json();
        if (isFunnelBundleUsable(bundle) === false) {
            throw new Error('Funnel bundle is missing a collection initiateFunnel needs');
        }

        return bundle;
    } finally {
        clearTimeout(abortTimer);
    }
};

// route-scoped bootstrap for the sales funnel and the encounter. Primary path: one cacheable request to
// getfunnelbootstrapapi (server-resolved program bundle, ~hundreds of KB) applied straight into
// Redux, with a per-program localStorage copy making warm reloads synchronous. Fallback path:
// the original parallel collection getters (~10MB of Firestore reads on a cold cache). The
// skipped/partial collections hydrate afterwards via getOrchestrator().
export const getFunnelBootstrap = (force = false, isCheckoutRoute = false, programSlug = null, programId = null, scope = 'intake') => {
    return async (dispatch, getState) => {
        try {
            let bundleApplied = false;
            // the sales funnel passes the slug from the url; the encounter funnel passes only the id
            // it read off the encounter, which the index resolves for free when the customer came
            // through the funnel. A cold visit has no index and lets the endpoint resolve it.
            const resolvedProgramSlug = isStringNotEmpty(programSlug) ? programSlug : getProgramSlugFromIndex(programId);
            const canBundle = isStringNotEmpty(resolvedProgramSlug) || isStringNotEmpty(programId);

            if (canBundle && isStringNotEmpty(FUNNEL_BUNDLE_API_URL) && force !== true) {
                const stored = isStringNotEmpty(resolvedProgramSlug) ? readStoredFunnelBundle(resolvedProgramSlug, scope) : null;
                if (isObjectNotEmpty(stored) && (Date.now() - (stored.storedAt ?? 0)) < FUNNEL_BUNDLE_FRESH_MS) {
                    // serve the cached bundle synchronously, then revalidate in the background
                    applyFunnelBundle(dispatch, stored.bundle);
                    bundleApplied = true;
                    fetchFunnelBundle(resolvedProgramSlug, programId, scope)
                        .then((freshBundle) => {
                            if (isObjectNotEmpty(freshBundle)) {
                                if (hasSameBundleVersions(stored.bundle, freshBundle) === false) {
                                    applyFunnelBundle(dispatch, freshBundle);
                                }

                                storeFunnelBundle(resolvedProgramSlug, freshBundle, scope);
                            }
                        })
                        .catch(() => { /* stale-but-usable bundle already applied */ });
                } else {
                    try {
                        const bundle = await fetchFunnelBundle(resolvedProgramSlug, programId, scope);
                        applyFunnelBundle(dispatch, bundle);
                        // the endpoint echoes the slug it resolved, so an id-only fetch still
                        // caches under the key the next slug lookup will use
                        storeFunnelBundle(bundle.programSlug ?? resolvedProgramSlug, bundle, scope);
                        bundleApplied = true;
                    } catch (bundleError) {
                        const failureReason = bundleError?.name === 'AbortError' ? 'timed out' : 'failed';
                        trackingConsole.warn(`Funnel bundle ${failureReason} (scope ${scope}); using collection getters:`, bundleError);
                        logger.logErrorEvent('FUNNEL_BUNDLE_FALLBACK', bundleError, `Funnel bundle ${failureReason} for scope ${scope}; using collection getters`);
                    }
                }
            }

            if (bundleApplied) {
                // agreements are not in the bundle; checkout deep links need them before paint
                if (isCheckoutRoute) {
                    await dispatch(getAgreements(force));
                }
            } else {
                // the funnel's intake bundle (on the way to /e) left partial products/variants/surveys under
                // fresh timestamps the getters below would hand straight back - no consult survey among them
                await dispatch(refreshBundleScopedCollections());

                const bootstrapLoads = [
                    dispatch(getPrograms(force)),
                    dispatch(getProducts(force)),
                    dispatch(getVariants(force)),
                    dispatch(getSurveys(force)),
                    dispatch(getProductFamilies(force)),
                    dispatch(getOfferGroups(force)),
                    dispatch(getFunnelChoiceSets(force)),
                    dispatch(getShippingClasses(force)),
                    dispatch(getShippingMethods(force)),
                    dispatch(getCampaigns(force)),
                ];

                // coveredEntities rides on every bundle, so the fallback loads it too for parity
                bootstrapLoads.push(dispatch(getCoveredEntities(force)));

                // agreements carry full legal text (~700KB) and only render at checkout; on /f the
                // post-paint full hydration loads them long before the customer reaches that step
                if (isCheckoutRoute) {
                    bootstrapLoads.push(dispatch(getAgreements(force)));
                }

                await Promise.all(bootstrapLoads);
            }

            const currentScope = getState()?.orchestrator?.bootstrapScope;
            if (currentScope !== 'FULL') {
                // a fallback whose forced refresh failed still holds the bundle's lists - keeping FUNNEL_BUNDLE is
                // what makes the next hydration force them again
                const isBundleScoped = bundleApplied || currentScope === 'FUNNEL_BUNDLE';
                dispatch(getOrchestratorSuccess({ bootstrapScope: isBundleScoped ? 'FUNNEL_BUNDLE' : 'FUNNEL' }));
            }

            return true;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return false;
    }
};

// after a bundle boot, the big three in Redux are program-scoped with fresh timestamps - the
// early-return guard would serve those partial lists forever, so full hydration must force them
export const refreshBundleScopedCollections = () => {
    return async (dispatch, getState) => {
        if (getState()?.orchestrator?.bootstrapScope !== 'FUNNEL_BUNDLE') {
            return;
        }

        const merged = {};
        const collect = (payload) => { Object.assign(merged, payload); };

        await Promise.all([
            dispatch(getProducts(true, null, collect)),
            dispatch(getVariants(true, null, collect)),
            dispatch(getSurveys(true, null, collect)),
        ]);

        // a getter collects its payload only when its load succeeded, so the lists stop being the bundle's only
        // once all three are in the merge - until then the scope stays FUNNEL_BUNDLE and the next call retries
        if (BUNDLE_SCOPED_COLLECTIONS.every((listKey) => Array.isArray(merged[listKey]))) {
            merged.bootstrapScope = 'FUNNEL';
        }

        // one commit for all three - a collection that failed simply is not in the merge, exactly as
        // it would not have dispatched on its own
        if (isObjectNotEmpty(merged)) {
            dispatch(getOrchestratorSuccess(merged));
        }
    };
};

// returns the already-loaded list from Redux when present and within the 24h TTL so repeat
// get*(false) calls are free; force and version-listener refreshes bail to null here so callers always hit the load path
const getFreshListFromState = (getState, listKey, force, remoteVersion) => {
    if (force === true || remoteVersion !== null) {
        return null;
    }

    try {
        const orchestratorState = typeof getState === 'function' ? getState()?.orchestrator : null;
        const list = orchestratorState?.[listKey];
        const lastUpdatedAtVal = orchestratorState?.[listKey + 'LastUpdatedAt'];
        // a legitimately-empty list with a fetch timestamp counts as fresh - requiring length > 0
        // would refetch empty collections on every repeat call.
        if (Array.isArray(list) && lastUpdatedAtVal) {
            const expiresAt = new Date(lastUpdatedAtVal);
            if (!isNaN(expiresAt.getTime())) {
                expiresAt.setHours(expiresAt.getHours() + 24);
                if (expiresAt > new Date()) {
                    return list;
                }
            }
        }
    } catch (e) {
        // fall through to the normal load path
    }

    return null;
};

// a bundle boot stamps the real version into Redux but deliberately never writes the disk caches,
// so a forced refresh that misses the cache has no cached version to keep. Falling back to 0 there
// makes the version listener read a mismatch and refetch the whole collection a second time, so the
// version already in state is the last resort before 0.
const resolveListVersion = (getState, listKey, remoteVersion, cachedVersion) => {
    if (remoteVersion != null) {
        return remoteVersion;
    }

    if (cachedVersion != null) {
        return cachedVersion;
    }

    return getState()?.orchestrator?.[listKey + 'Version'] ?? 0;
};

// getters commit their own payload by default. A caller loading several at once can pass onPayload
// instead and commit them together - each collection lands on its own Firestore round trip, so
// committing on arrival re-renders every consumer once per collection.
const commitOrCollect = (dispatch, onPayload, payload) => {
    if (typeof onPayload === 'function') {
        onPayload(payload);
        return;
    }

    dispatch(getOrchestratorSuccess(payload));
};

// fire-and-forget cache write; empty lists are never persisted (a spurious offline-empty read
// would pin for 24h) - an authoritative empty (force/version refresh) clears the cache instead
const persistListToCache = (listKey, list, listLastUpdatedAt, listVersion, isAuthoritative = false) => {
    if (isListEmpty(list)) {
        if (isAuthoritative === true) {
            setTimeout(() => {
                try {
                    localStorage.removeItem(listKey);
                    localStorage.removeItem(listKey + 'LastUpdatedAt');
                    localStorage.removeItem(listKey + 'Version');
                } catch (cacheError) {
                    trackingConsole.warn('Failed to clear ' + listKey + ' cache from localStorage:', cacheError);
                }
            }, 0);
        }
        return;
    }

    setTimeout(() => {
        try {
            localStorage.setItem(listKey, JSON.stringify(list));
            localStorage.setItem(listKey + 'LastUpdatedAt', listLastUpdatedAt.toString());
            localStorage.setItem(listKey + 'Version', String(listVersion));
        } catch (cacheError) {
            trackingConsole.warn('Failed to persist ' + listKey + ' cache to localStorage:', cacheError);
        }
    }, 0);
};

export const getCoveredEntities = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'coveredEntities', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('coveredEntities');
                let listLastUpdatedAtString = localStorage.getItem('coveredEntitiesLastUpdatedAt');
                let listVersionString = localStorage.getItem('coveredEntitiesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = parseInt(listVersionString, 10) ?? null;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await CoveredEntity.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'coveredEntities', remoteVersion, listVersion);
                    persistListToCache('coveredEntities', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for coveredEntities', err);
                list = await CoveredEntity.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ coveredEntities: list, coveredEntitiesLastUpdatedAt: listLastUpdatedAt, coveredEntitiesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getProviders = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'providers', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('providers');
                let listLastUpdatedAtString = localStorage.getItem('providersLastUpdatedAt');
                let listVersionString = localStorage.getItem('providersVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Provider.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'providers', remoteVersion, listVersion);
                    persistListToCache('providers', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for providers', err);
                list = await Provider.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ providers: list, providersLastUpdatedAt: listLastUpdatedAt, providersVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getProducts = (force = false, remoteVersion = null, onPayload = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'products', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                const cached = await readCache("products");
                if (cached) {
                    list = cached.list || [];
                    listLastUpdatedAt = cached.lastUpdatedAt ? new Date(cached.lastUpdatedAt) : null;
                    listVersion = cached.version ?? null;
                }
                // let listString = localStorage.getItem('products');
                // let listLastUpdatedAtString = localStorage.getItem('productsLastUpdatedAt');
                // let listVersionString = localStorage.getItem('productsVersion');
                // if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                //     // already there so load into redux
                //     list = JSON.parse(listString);
                //     listLastUpdatedAt = new Date(listLastUpdatedAtString);
                //     listVersion = listVersionString;
                // }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Product.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'products', remoteVersion, listVersion);
                    // localStorage.setItem('products', JSON.stringify(list));
                    // localStorage.setItem('productsLastUpdatedAt', listLastUpdatedAt.toString());
                    // localStorage.setItem('productsVersion', String(listVersion));
                    // Store to IndexedDB
                    if (isListNotEmpty(list)) {
                        // never persist a (possibly spurious) empty list - see persistListToCache
                        writeCache("products", {
                            list,
                            lastUpdatedAt: listLastUpdatedAt.toISOString(),
                            version: listVersion
                        }).catch((cacheError) => {
                            trackingConsole.warn('Failed to persist products cache to IndexedDB:', cacheError);
                        });
                    } else if (force === true || remoteVersion !== null) {
                        // authoritative empty - drop the stale cached copy (see persistListToCache)
                        clearCache("products").catch((cacheError) => {
                            trackingConsole.warn('Failed to clear products cache from IndexedDB:', cacheError);
                        });
                    }
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for products', err);
                list = await Product.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            commitOrCollect(dispatch, onPayload, { products: orderBy(list, ['name'], ['asc']), productsLastUpdatedAt: listLastUpdatedAt, productsVersion: listVersion });

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getVariants = (force = false, remoteVersion = null, onPayload = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'variants', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                const cached = await readCache("variants");
                if (cached) {
                    list = cached.list || [];
                    listLastUpdatedAt = cached.lastUpdatedAt ? new Date(cached.lastUpdatedAt) : null;
                    listVersion = cached.version ?? null;
                }
                // let listString = localStorage.getItem('variants');
                // let listLastUpdatedAtString = localStorage.getItem('variantsLastUpdatedAt');
                // let listVersionString = localStorage.getItem('variantsVersion');
                // if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                //     // already there so load into redux
                //     list = JSON.parse(listString);
                //     listLastUpdatedAt = new Date(listLastUpdatedAtString);
                //     listVersion = listVersionString;
                // }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ProductVariant.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'variants', remoteVersion, listVersion);
                    // localStorage.setItem('variants', JSON.stringify(list));
                    // localStorage.setItem('variantsLastUpdatedAt', listLastUpdatedAt.toString());
                    // localStorage.setItem('variantsVersion', String(listVersion));
                    if (isListNotEmpty(list)) {
                        // never persist a (possibly spurious) empty list - see persistListToCache
                        writeCache("variants", {
                            list,
                            lastUpdatedAt: listLastUpdatedAt.toISOString(),
                            version: listVersion
                        }).catch((cacheError) => {
                            trackingConsole.warn('Failed to persist variants cache to IndexedDB:', cacheError);
                        });
                    } else if (force === true || remoteVersion !== null) {
                        // authoritative empty - drop the stale cached copy (see persistListToCache)
                        clearCache("variants").catch((cacheError) => {
                            trackingConsole.warn('Failed to clear variants cache from IndexedDB:', cacheError);
                        });
                    }
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for variants', err);
                list = await ProductVariant.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            commitOrCollect(dispatch, onPayload, { variants: orderBy(list, ['name'], ['asc']), variantsLastUpdatedAt: listLastUpdatedAt, variantsVersion: listVersion });

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getProductCategories = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'productCategories', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('productCategories');
                let listLastUpdatedAtString = localStorage.getItem('productCategoriesLastUpdatedAt');
                let listVersionString = localStorage.getItem('productCategoriesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ProductCategory.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'productCategories', remoteVersion, listVersion);
                    persistListToCache('productCategories', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for product categories', err);
                list = await ProductCategory.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ productCategories: list, productCategoriesLastUpdatedAt: listLastUpdatedAt, productCategoriesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getAgreements = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'agreements', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('agreements');
                let listLastUpdatedAtString = localStorage.getItem('agreementsLastUpdatedAt');
                let listVersionString = localStorage.getItem('agreementsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Agreement.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'agreements', remoteVersion, listVersion);
                    persistListToCache('agreements', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for agreements', err);
                list = await Agreement.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ agreements: list, agreementsLastUpdatedAt: listLastUpdatedAt, agreementsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

// loads ALL surveys, including deactivated ones - do NOT add a where('active','==',true) filter
// here. Encounter consult/follow-up pages resolve a survey BY ID (getSurveyInfo(surveyId, surveys))
// from the recorded form on historical encounters, and deactivated surveys are the older
// versions those encounters still reference; dropping them would break clinical review. Only the
// funnel bundle endpoint filters to active, and only for forward-looking by-slug resolution.
export const getSurveys = (force = false, remoteVersion = null, onPayload = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'surveys', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                const cached = await readCache("surveys");
                if (cached) {
                    list = cached.list || [];
                    listLastUpdatedAt = cached.lastUpdatedAt ? new Date(cached.lastUpdatedAt) : null;
                    listVersion = cached.version ?? null;
                }
                // let listString = localStorage.getItem('surveys');
                // let listLastUpdatedAtString = localStorage.getItem('surveysLastUpdatedAt');
                // let listVersionString = localStorage.getItem('surveysVersion');
                // if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                //     // already there so load into redux
                //     list = JSON.parse(listString);
                //     listLastUpdatedAt = new Date(listLastUpdatedAtString);
                //     listVersion = listVersionString;
                // }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Survey.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'surveys', remoteVersion, listVersion);
                    // localStorage.setItem('surveys', JSON.stringify(list));
                    // localStorage.setItem('surveysLastUpdatedAt', listLastUpdatedAt.toString());
                    // localStorage.setItem('surveysVersion', String(listVersion));
                    if (isListNotEmpty(list)) {
                        // never persist a (possibly spurious) empty list - see persistListToCache
                        writeCache("surveys", {
                            list,
                            lastUpdatedAt: listLastUpdatedAt.toISOString(),
                            version: listVersion
                        }).catch((cacheError) => {
                            trackingConsole.warn('Failed to persist surveys cache to IndexedDB:', cacheError);
                        });
                    } else if (force === true || remoteVersion !== null) {
                        // authoritative empty - drop the stale cached copy (see persistListToCache)
                        clearCache("surveys").catch((cacheError) => {
                            trackingConsole.warn('Failed to clear surveys cache from IndexedDB:', cacheError);
                        });
                    }
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for surveys', err);
                list = await Survey.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            commitOrCollect(dispatch, onPayload, { surveys: list, surveysLastUpdatedAt: listLastUpdatedAt, surveysVersion: listVersion });

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getConditions = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'conditions', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('conditions');
                let listLastUpdatedAtString = localStorage.getItem('conditionsLastUpdatedAt');
                let listVersionString = localStorage.getItem('conditionsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Condition.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'conditions', remoteVersion, listVersion);
                    persistListToCache('conditions', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for conditions', err);
                list = await Condition.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ conditions: list, conditionsLastUpdatedAt: listLastUpdatedAt, conditionsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getTests = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'tests', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('tests');
                let listLastUpdatedAtString = localStorage.getItem('testsLastUpdatedAt');
                let listVersionString = localStorage.getItem('testsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Product.get({ productType: ['TEST', 'TEST_SUBSCRIPTION'] });
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'tests', remoteVersion, listVersion);
                    persistListToCache('tests', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for tests', err);
                list = await Product.getAll({ productType: ['TEST', 'TEST_SUBSCRIPTION'] });
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ tests: list, testsLastUpdatedAt: listLastUpdatedAt, testsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getVaccinations = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'vaccinations', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('vaccinations');
                let listLastUpdatedAtString = localStorage.getItem('vaccinationsLastUpdatedAt');
                let listVersionString = localStorage.getItem('vaccinationsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Product.get({ productType: ['VACCINATION', 'VACCINATION_SUBSCRIPTION'] });
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'vaccinations', remoteVersion, listVersion);
                    persistListToCache('vaccinations', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for vaccinations', err);
                list = await Product.get({ productType: ['VACCINATION', 'VACCINATION_SUBSCRIPTION'] });
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ vaccinations: list, vaccinationsLastUpdatedAt: listLastUpdatedAt, vaccinationsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getMetrics = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'metrics', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('metrics');
                let listLastUpdatedAtString = localStorage.getItem('metricsLastUpdatedAt');
                let listVersionString = localStorage.getItem('metricsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Biomarker.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'metrics', remoteVersion, listVersion);
                    persistListToCache('metrics', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for metrics', err);
                list = await Biomarker.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ metrics: list, metricsLastUpdatedAt: listLastUpdatedAt, metricsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getShippingClasses = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'shippingClasses', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('shippingClasses');
                let listLastUpdatedAtString = localStorage.getItem('shippingClassesLastUpdatedAt');
                let listVersionString = localStorage.getItem('shippingClassesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ShippingClass.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'shippingClasses', remoteVersion, listVersion);
                    persistListToCache('shippingClasses', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for shipping classes', err);
                list = await ShippingClass.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ shippingClasses: list, shippingClassesLastUpdatedAt: listLastUpdatedAt, shippingClassesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getShippingMethods = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'shippingMethods', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('shippingMethods');
                let listLastUpdatedAtString = localStorage.getItem('shippingMethodsLastUpdatedAt');
                let listVersionString = localStorage.getItem('shippingMethodsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ShippingMethod.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'shippingMethods', remoteVersion, listVersion);
                    persistListToCache('shippingMethods', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for shipping methods', err);
                list = await ShippingMethod.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ shippingMethods: list, shippingMethodsLastUpdatedAt: listLastUpdatedAt, shippingMethodsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getCampaigns = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'campaigns', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('campaigns');
                let listLastUpdatedAtString = localStorage.getItem('campaignsLastUpdatedAt');
                let listVersionString = localStorage.getItem('campaignsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Campaign.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'campaigns', remoteVersion, listVersion);
                    persistListToCache('campaigns', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for campaigns', err);
                list = await Campaign.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ campaigns: list, campaignsLastUpdatedAt: listLastUpdatedAt, campaignsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getProductFamilies = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'productFamilies', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('productFamilies');
                let listLastUpdatedAtString = localStorage.getItem('productFamiliesLastUpdatedAt');
                let listVersionString = localStorage.getItem('productFamiliesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ProductFamily.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'productFamilies', remoteVersion, listVersion);
                    persistListToCache('productFamilies', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for productFamilies', err);
                list = await ProductFamily.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ productFamilies: orderBy(list, ['name'], ['asc']), productFamiliesLastUpdatedAt: listLastUpdatedAt, productFamiliesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getOfferGroups = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'offerGroups', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('offerGroups');
                let listLastUpdatedAtString = localStorage.getItem('offerGroupsLastUpdatedAt');
                let listVersionString = localStorage.getItem('offerGroupsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await OfferGroup.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'offerGroups', remoteVersion, listVersion);
                    persistListToCache('offerGroups', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for offerGroups', err);
                list = await OfferGroup.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ offerGroups: orderBy(list, ['name'], ['asc']), offerGroupsLastUpdatedAt: listLastUpdatedAt, offerGroupsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getCommercialEquivalencyGroups = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'commercialEquivalencyGroups', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('commercialEquivalencyGroups');
                let listLastUpdatedAtString = localStorage.getItem('commercialEquivalencyGroupsLastUpdatedAt');
                let listVersionString = localStorage.getItem('commercialEquivalencyGroupsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await CommercialEquivalencyGroup.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'commercialEquivalencyGroups', remoteVersion, listVersion);
                    persistListToCache('commercialEquivalencyGroups', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for commercialEquivalencyGroups', err);
                list = await CommercialEquivalencyGroup.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ commercialEquivalencyGroups: orderBy(list, ['name'], ['asc']), commercialEquivalencyGroupsLastUpdatedAt: listLastUpdatedAt, commercialEquivalencyGroupsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getFunnelChoiceSets = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'funnelChoiceSets', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('funnelChoiceSets');
                let listLastUpdatedAtString = localStorage.getItem('funnelChoiceSetsLastUpdatedAt');
                let listVersionString = localStorage.getItem('funnelChoiceSetsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await FunnelChoiceSet.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'funnelChoiceSets', remoteVersion, listVersion);
                    persistListToCache('funnelChoiceSets', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for funnelChoiceSets', err);
                list = await FunnelChoiceSet.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ funnelChoiceSets: orderBy(list, ['name'], ['asc']), funnelChoiceSetsLastUpdatedAt: listLastUpdatedAt, funnelChoiceSetsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getPrograms = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'programs', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('programs');
                let listLastUpdatedAtString = localStorage.getItem('programsLastUpdatedAt');
                let listVersionString = localStorage.getItem('programsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Program.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'programs', remoteVersion, listVersion);
                    persistListToCache('programs', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for programs', err);
                list = await Program.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ programs: list, programsLastUpdatedAt: listLastUpdatedAt, programsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getAffiliates = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'affiliates', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('affiliates');
                let listLastUpdatedAtString = localStorage.getItem('affiliatesLastUpdatedAt');
                let listVersionString = localStorage.getItem('affiliatesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Affiliate.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'affiliates', remoteVersion, listVersion);
                    persistListToCache('affiliates', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for affiliates', err);
                list = await Affiliate.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ affiliates: list, affiliatesLastUpdatedAt: listLastUpdatedAt, affiliatesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getPartners = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'partners', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('partners');
                let listLastUpdatedAtString = localStorage.getItem('partnersLastUpdatedAt');
                let listVersionString = localStorage.getItem('partnersVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Partner.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'partners', remoteVersion, listVersion);
                    persistListToCache('partners', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for partners', err);
                list = await Partner.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ partners: list, partnersLastUpdatedAt: listLastUpdatedAt, partnersVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getArticles = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'articles', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('articles');
                let listLastUpdatedAtString = localStorage.getItem('articlesLastUpdatedAt');
                let listVersionString = localStorage.getItem('articlesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Article.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'articles', remoteVersion, listVersion);
                    persistListToCache('articles', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for articles', err);
                list = await Article.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ articles: list, articlesLastUpdatedAt: listLastUpdatedAt, articlesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getArticleCategories = (force = false, remoteVersion = null) => {
    return async (dispatch, getState) => {
        const freshList = getFreshListFromState(getState, 'articleCategories', force, remoteVersion);
        if (freshList !== null) { return freshList; }

        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('articleCategories');
                let listLastUpdatedAtString = localStorage.getItem('articleCategoriesLastUpdatedAt');
                let listVersionString = localStorage.getItem('articleCategoriesVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await ArticleCategory.getAll();
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'articleCategories', remoteVersion, listVersion);
                    persistListToCache('articleCategories', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for article categories', err);
                list = await ArticleCategory.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ articleCategories: list, articleCategoriesLastUpdatedAt: listLastUpdatedAt, articleCategoriesVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const getEventsInRange = (force = false, start = moment().utc().toDate(), end = moment().add(30, 'days').utc().toDate(), remoteVersion = null) => {
    return async (dispatch, getState) => {
        try {
            const now = new Date();
            let list = [];
            let listLastUpdatedAt = null;
            let listVersion = null;
            try {
                let listString = localStorage.getItem('events');
                let listLastUpdatedAtString = localStorage.getItem('eventsLastUpdatedAt');
                let listVersionString = localStorage.getItem('eventsVersion');
                if (isStringNotEmpty(listString) && isStringNotEmpty(listLastUpdatedAtString) && isStringNotEmpty(listVersionString)) {
                    // already there so load into redux
                    list = JSON.parse(listString);
                    listLastUpdatedAt = new Date(listLastUpdatedAtString);
                    listVersion = listVersionString;
                }

                if (force === true || listVersion === null || (remoteVersion !== null && listVersion !== remoteVersion) || listLastUpdatedAt === null || (listLastUpdatedAt.getTime() + 24 * 60 * 60 * 1000) < now.getTime()) {
                    list = await Event.getPaginated({ start, end }, 1000, null, null, 'startDate', 'startDate');
                    listLastUpdatedAt = now;
                    listVersion = resolveListVersion(getState, 'events', remoteVersion, listVersion);
                    persistListToCache('events', list, listLastUpdatedAt, listVersion, force === true || remoteVersion !== null);
                }
            } catch (err) {
                trackingConsole.log('orchestrator error for events', err);
                list = await Event.getAll();
                listLastUpdatedAt = now;
                listVersion = remoteVersion != null ? remoteVersion : listVersion;
            }

            dispatch(getOrchestratorSuccess({ events: list, eventsLastUpdatedAt: listLastUpdatedAt, eventsVersion: listVersion }));

            return list;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

const TABLES_CONFIG = {
    coveredEntities: {
        stateVersionKey: "coveredEntitiesVersion",
        thunk: getCoveredEntities,
    },
    providers: {
        stateVersionKey: "providersVersion",
        thunk: getProviders,
    },
    products: {
        stateVersionKey: "productsVersion",
        thunk: getProducts,
    },
    variants: {
        stateVersionKey: "variantsVersion",
        thunk: getVariants,
    },
    productCategories: {
        stateVersionKey: "productCategoriesVersion",
        thunk: getProductCategories,
    },
    agreements: {
        stateVersionKey: "agreementsVersion",
        thunk: getAgreements,
    },
    surveys: {
        stateVersionKey: "surveysVersion",
        thunk: getSurveys,
    },
    // conditions: {
    //     stateVersionKey: "conditionsVersion",
    //     thunk: getConditions,
    // },
    // tests: {
    //     stateVersionKey: "testsVersion",
    //     thunk: getTests,
    // },
    // vaccinations: {
    //     stateVersionKey: "vaccinationsVersion",
    //     thunk: getVaccinations,
    // },
    // metrics: {
    //     stateVersionKey: "metricsVersion",
    //     thunk: getMetrics,
    // },
    shippingClasses: {
        stateVersionKey: "shippingClassesVersion",
        thunk: getShippingClasses,
    },
    shippingMethods: {
        stateVersionKey: "shippingMethodsVersion",
        thunk: getShippingMethods,
    },
    campaigns: {
        stateVersionKey: "campaignsVersion",
        thunk: getCampaigns,
    },
    productFamilies: {
        stateVersionKey: "productFamiliesVersion",
        thunk: getProductFamilies,
    },
    offerGroups: {
        stateVersionKey: "offerGroupsVersion",
        thunk: getOfferGroups,
    },
    commercialEquivalencyGroups: {
        stateVersionKey: "commercialEquivalencyGroupsVersion",
        thunk: getCommercialEquivalencyGroups,
    },
    funnelChoiceSets: {
        stateVersionKey: "funnelChoiceSetsVersion",
        thunk: getFunnelChoiceSets,
    },
    programs: {
        stateVersionKey: "programsVersion",
        thunk: getPrograms,
    },
    // affiliates: {
    //     stateVersionKey: "affiliatesVersion",
    //     thunk: getAffiliates,
    // },
    partners: {
        stateVersionKey: "partnersVersion",
        thunk: getPartners,
    },
    // articles: {
    //     stateVersionKey: "articlesVersion",
    //     thunk: getArticles,
    // },
    // articleCategories: {
    //     stateVersionKey: "articleCategoriesVersion",
    //     thunk: getArticleCategories,
    // },
    events: {
        stateVersionKey: "eventsVersion",
        // for events, we may want custom args – we'll handle that separately
        thunk: getEventsInRange,
    },
};

// This function is called once at app startup, after the store is created
export const initOrchestratorVersionListeners = (store) => {
    const { dispatch, getState } = store;

    const unsubscribes = [];

    trackingConsole.log('INITIATING ORCHESTRATOR VERSION LISTENERS');

    Object.entries(TABLES_CONFIG).forEach(([tableKey, config]) => {
        const { stateVersionKey, thunk } = config;

        const ref = doc(db, "orchestratorVersions", tableKey);

        const unsub = onSnapshot(ref, (snap) => {
            if (!snap.exists()) return;

            const data = snap.data();
            const remoteVersion = data.version;
            if (remoteVersion == null) return;

            const state = getState();
            const localVersionString = state.orchestrator[stateVersionKey];
            const parsedLocalVersion = parseInt(localVersionString, 10);
            const localVersion = Number.isNaN(parsedLocalVersion) ? null : parsedLocalVersion;
            trackingConsole.log(`[Orchestrator] ${tableKey} local version:`, localVersion, "remote version:", remoteVersion);

            // a collection the app never loaded has no local version, and that is an absence rather
            // than a mismatch - fetching it here drags the collections that are deliberately loaded
            // on demand (partners at sign-in, events from the admin modal) into every boot. Once a
            // consumer loads one, its version is stamped and this listener keeps it fresh normally.
            if (localVersion === null) {
                return;
            }

            // If versions differ, force refresh that table
            if (localVersion !== remoteVersion) {
                trackingConsole.log(
                    `[Orchestrator] ${tableKey} version changed:`,
                    localVersion,
                    "→",
                    remoteVersion
                );

                if (tableKey === "events") {
                    // events thunk has (force, start, end) signature
                    // you can keep your default (now -> now+30 days) by passing undefined
                    dispatch(thunk(true, undefined, undefined, remoteVersion));
                } else {
                    dispatch(thunk(true, remoteVersion));
                }
            }
        });

        unsubscribes.push(unsub);
    });

    return () => {
        unsubscribes.forEach((u) => u && u());
    };
};

//#endregion
//#region Initial State

const initialState = {
    // null until a bootstrap completes; 'FUNNEL' = funnel-critical collections only, 'FUNNEL_BUNDLE' = the same
    // with products/variants/surveys trimmed to one program by a bundle, 'FULL' = everything
    bootstrapScope: null,

    coveredEntities: [],
    coveredEntitiesLastUpdatedAt: null,
    coveredEntitiesVersion: null,

    providers: [],
    providersLastUpdatedAt: null,
    providersVersion: null,

    products: [],
    productsLastUpdatedAt: null,
    productsVersion: null,

    variants: [],
    variantsLastUpdatedAt: null,
    variantsVersion: null,

    productCategories: [],
    productCategoriesLastUpdatedAt: null,
    productCategoriesVersion: null,

    agreements: [],
    agreementsLastUpdatedAt: null,
    agreementsVersion: null,

    surveys: [],
    surveysLastUpdatedAt: null,
    surveysVersion: null,

    conditions: [],
    conditionsLastUpdatedAt: null,
    conditionsVersion: null,

    tests: [],
    testsLastUpdatedAt: null,
    testsVersion: null,

    vaccinations: [],
    vaccinationsLastUpdatedAt: null,
    vaccinationsVersion: null,

    metrics: [],
    metricsLastUpdatedAt: null,
    metricsVersion: null,

    shippingClasses: [],
    shippingClassesLastUpdatedAt: null,
    shippingClassesVersion: null,

    shippingMethods: [],
    shippingMethodsLastUpdatedAt: null,
    shippingMethodsVersion: null,

    campaigns: [],
    campaignsLastUpdatedAt: null,
    campaignsVersion: null,

    productFamilies: [],
    productFamiliesLastUpdatedAt: null,
    productFamiliesVersion: null,

    offerGroups: [],
    offerGroupsLastUpdatedAt: null,
    offerGroupsVersion: null,

    commercialEquivalencyGroups: [],
    commercialEquivalencyGroupsLastUpdatedAt: null,
    commercialEquivalencyGroupsVersion: null,

    funnelChoiceSets: [],
    funnelChoiceSetsLastUpdatedAt: null,
    funnelChoiceSetsVersion: null,

    programs: [],
    programsLastUpdatedAt: null,
    programsVersion: null,

    affiliates: [],
    affiliatesLastUpdatedAt: null,
    affiliatesVersion: null,

    articles: [],
    articlesLastUpdatedAt: null,
    articlesVersion: null,

    articleCategories: [],
    articleCategoriesLastUpdatedAt: null,
    articleCategoriesVersion: null,

    events: [],
    eventsLastUpdatedAt: null,
    eventsVersion: null,
};

//#endregion
//#region Reducer

const orchestratorReducer = (state = initialState, action) => {
    switch (action.type) {
        case FETCH_ORCHESTRATOR_SUCCESS: return fetchRecordsSuccess(state, action.payload);

        default:
            return state;
    }
};

//#endregion

// module scope so reselect memoization survives across renders, and each keys on the collections it
// reads - the whole orchestrator slice gets a new identity on every dispatch (updateObject spreads),
// which recomputed all of these and handed consumers a fresh array identity on every store change
const selectCoveredEntityById = createSelector((state) => state.orchestrator.coveredEntities, (_, id) => id, (coveredEntities, id) => getCoveredEntityInfo(id, coveredEntities));
const selectCoveredEntityBySlug = createSelector((state) => state.orchestrator.coveredEntities, (_, slug) => slug, (coveredEntities, slug) => getCoveredEntityBySlug(slug, coveredEntities));
const selectProductBySku = createSelector((state) => state.orchestrator.products, (_, productSku) => productSku, (products, productSku) => getProductBySku(productSku, products));
const selectProductByShortSku = createSelector((state) => state.orchestrator.products, (_, productShortSku) => productShortSku, (products, productShortSku) => getProductByShortSku(productShortSku, products));
const selectProductBySlug = createSelector((state) => state.orchestrator.products, (_, slug) => slug, (products, slug) => getProductBySlug(slug, products));
const selectAgreementByKey = createSelector((state) => state.orchestrator.agreements, (_, key) => key, (agreements, key) => getAgreementByKey(key, agreements));
const selectSurveyById = createSelector((state) => state.orchestrator.surveys, (_, id) => id, (surveys, id) => getSurveyInfo(id, surveys));
const selectSurveyBySlug = createSelector((state) => state.orchestrator.surveys, (_, slug) => slug, (surveys, slug) => getSurveyBySlug(slug, surveys));
const selectSurveysByProductSku = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.surveys, (_, productSku) => productSku, (products, surveys, productSku) => getSurveysByProductSku(productSku, products, surveys));
const selectSurveysByCampaignId = createSelector((state) => state.orchestrator.campaigns, (state) => state.orchestrator.surveys, (_, campaignId) => campaignId, (campaigns, surveys, campaignId) => getSurveysByCampaignId(campaignId, campaigns, surveys));
const selectSurveysByCampaignSlug = createSelector((state) => state.orchestrator.campaigns, (state) => state.orchestrator.surveys, (_, campaignSlug) => campaignSlug, (campaigns, surveys, campaignSlug) => getSurveysByCampaignSlug(campaignSlug, campaigns, surveys));
const selectSurveysByProgramId = createSelector((state) => state.orchestrator.programs, (state) => state.orchestrator.surveys, (_, programId) => programId, (programs, surveys, programId) => getSurveysByProgramId(programId, programs, surveys));
const selectSurveysByProgramSlug = createSelector((state) => state.orchestrator.programs, (state) => state.orchestrator.surveys, (_, programSlug) => programSlug, (programs, surveys, programSlug) => getSurveysByProgramSlug(programSlug, programs, surveys));
const selectCondition = createSelector((state) => state.orchestrator.conditions, (_, id) => id, (conditions, id) => getConditionInfo(id, conditions));
const selectTest = createSelector((state) => state.orchestrator.tests, (_, id) => id, (tests, id) => getTestInfo(id, tests));
const selectVaccination = createSelector((state) => state.orchestrator.vaccinations, (_, id) => id, (vaccinations, id) => getVaccinationInfo(id, vaccinations));
const selectMetric = createSelector((state) => state.orchestrator.metrics, (_, id) => id, (metrics, id) => getMetricInfo(id, metrics));
const selectCampaignById = createSelector((state) => state.orchestrator.campaigns, (_, id) => id, (campaigns, id) => getCampaignById(id, campaigns));
const selectCampaignBySlug = createSelector((state) => state.orchestrator.campaigns, (_, slug) => slug, (campaigns, slug) => getCampaignBySlug(slug, campaigns));
const selectCampaignByProgramSlug = createSelector((state) => state.orchestrator.campaigns, (state) => state.orchestrator.programs, (_, programSlug) => programSlug, (campaigns, programs, programSlug) => getCampaignByProgramSlug(programSlug, campaigns, programs));
export const selectDefaultCampaignByProgramSlug = createSelector((state) => state.orchestrator.campaigns, (state) => state.orchestrator.programs, (_, programSlug) => programSlug, (_, programSlug, campaignSlug) => campaignSlug, (campaigns, programs, programSlug, campaignSlug) => getDefaultCampaign(programSlug, campaignSlug, campaigns, programs));
export const selectDefaultProductByProgramSlug = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.programs, (_, programSlug) => programSlug, (_, programSlug, productSku) => productSku, (products, programs, programSlug, productSku) => getDefaultProduct(programSlug, productSku, products, programs));
const selectProductsByProgramId = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.programs, (_, programId) => programId, (products, programs, programId) => getProductsByProgramId(programId, products, programs));
export const selectProductsByProgramSlug = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.programs, (_, programSlug) => programSlug, (products, programs, programSlug) => getProductsByProgramSlug(programSlug, products, programs));
const selectProductFamilyById = createSelector((state) => state.orchestrator.productFamilies, (_, id) => id, (productFamilies, id) => getProductFamilyById(id, productFamilies));
const selectOfferGroupById = createSelector((state) => state.orchestrator.offerGroups, (_, id) => id, (offerGroups, id) => getOfferGroupById(id, offerGroups));
const selectOfferGroupByToken = createSelector((state) => state.orchestrator.offerGroups, (_, token) => token, (offerGroups, token) => getOfferGroupByToken(token, offerGroups));
const selectCommercialEquivalencyGroupById = createSelector((state) => state.orchestrator.commercialEquivalencyGroups, (_, id) => id, (commercialEquivalencyGroups, id) => getCommercialEquivalencyGroupById(id, commercialEquivalencyGroups));
const selectFunnelChoiceSetById = createSelector((state) => state.orchestrator.funnelChoiceSets, (_, id) => id, (funnelChoiceSets, id) => getFunnelChoiceSetById(id, funnelChoiceSets));

const selectProgramById = createSelector((state) => state.orchestrator.programs, (_, id) => id, (programs, id) => getProgramById(id, programs));
export const selectProgramBySlug = createSelector((state) => state.orchestrator.programs, (_, slug) => slug, (programs, slug) => getProgramBySlug(slug, programs));

const selectAffiliateById = createSelector((state) => state.orchestrator.affiliates, (_, id) => id, (affiliates, id) => getAffiliateById(id, affiliates));
const selectPartnerById = createSelector((state) => state.orchestrator.partners, (_, id) => id, (partners, id) => getPartnerById(id, partners));

const selectProductCategory = createSelector((state) => state.orchestrator.productCategories, (_, id) => id, (productCategories, id) => getProductCategoryById(id, productCategories));
export const selectProductCategoryBySlug = createSelector((state) => state.orchestrator.productCategories, (_, slug) => slug, (productCategories, slug) => getProductCategoryBySlug(slug, productCategories));
const selectProductCategoriesBySlugs = createSelector((state) => state.orchestrator.productCategories, (_, slugs) => slugs, (productCategories, slugs) => getProductCategoriesBySlugs(slugs, productCategories));
const selectChildProductCategories = createSelector((state) => state.orchestrator.productCategories, (_, parentId) => parentId, (productCategories, parentId) => getChildProductCategoriesByParentId(parentId, productCategories));
const selectProductsByCategoryId = createSelector((state) => state.orchestrator.products, (_, categoryId) => categoryId, (products, categoryId) => getProductsByCategoryId(categoryId, products));
const selectProductsByCategorySlug = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.productCategories, (_, categorySlug) => categorySlug, (products, productCategories, categorySlug) => getProductsByCategorySlug(categorySlug, products, productCategories));
const selectProductCategoriesByProductId = createSelector((state) => state.orchestrator.products, (state) => state.orchestrator.productCategories, (_, productId) => productId, (products, productCategories, productId) => getProductCategoriesByProductId(productId, products, productCategories));

const selectVariantsByProductId = createSelector((state) => state.orchestrator.variants, (_, productId) => productId, (variants, productId) => getVariantsByProductId(productId, variants));
const selectVariantsByProductSlug = createSelector((state) => state.orchestrator.variants, (state) => state.orchestrator.products, (_, productSlug) => productSlug, (variants, products, productSlug) => getVariantsByProductSlug(productSlug, variants, products));
const selectVariantsByProductSku = createSelector((state) => state.orchestrator.variants, (state) => state.orchestrator.products, (_, productSku) => productSku, (variants, products, productSku) => getVariantsByProductSku(productSku, variants, products));
const selectVariantById = createSelector((state) => state.orchestrator.variants, (_, id) => id, (variants, id) => getVariantById(id, variants));
const selectVariantBySlug = createSelector((state) => state.orchestrator.variants, (_, slug) => slug, (variants, slug) => getVariantBySlug(slug, variants));
const selectVariantBySku = createSelector((state) => state.orchestrator.variants, (_, sku) => sku, (variants, sku) => getVariantBySku(sku, variants));

const selectProviderByUserId = createSelector((state) => state.orchestrator.providers, (_, userId) => userId, (providers, userId) => getProviderByUserId(userId, providers));

const selectArticleById = createSelector((state) => state.orchestrator.articles, (_, id) => id, (articles, id) => getArticleById(id, articles));
const selectArticleBySlug = createSelector((state) => state.orchestrator.articles, (_, slug) => slug, (articles, slug) => getArticleBySlug(slug, articles));
const selectArticleCategory = createSelector((state) => state.orchestrator.articleCategories, (_, id) => id, (articleCategories, id) => getArticleCategoryById(id, articleCategories));
const selectArticleCategoryBySlug = createSelector((state) => state.orchestrator.articleCategories, (_, slug) => slug, (articleCategories, slug) => getArticleCategoryBySlug(slug, articleCategories));
const selectChildArticleCategories = createSelector((state) => state.orchestrator.articleCategories, (_, parentId) => parentId, (articleCategories, parentId) => getChildArticleCategoriesByParentId(parentId, articleCategories));
const selectArticlesByCategoryId = createSelector((state) => state.orchestrator.articles, (_, categoryId) => categoryId, (articles, categoryId) => getArticlesByCategoryId(categoryId, articles));
const selectArticlesByCategorySlug = createSelector((state) => state.orchestrator.articles, (state) => state.orchestrator.articleCategories, (_, categorySlug) => categorySlug, (articles, articleCategories, categorySlug) => getArticlesByCategorySlug(categorySlug, articles, articleCategories));
const selectArticleCategoryByArticleId = createSelector((state) => state.orchestrator.articles, (state) => state.orchestrator.articleCategories, (_, articleId) => articleId, (articles, articleCategories, articleId) => getArticleCategoryByArticleId(articleId, articles, articleCategories));

const selectEventById = createSelector((state) => state.orchestrator.events, (_, id) => id, (events, id) => getEventById(id, events));

export const useOrchestrator = () => {
    const dispatch = useDispatch();

    return {
        coveredEntities: useSelector((appState) => appState.orchestrator.coveredEntities),
        providers: useSelector((appState) => appState.orchestrator.providers),
        products: useSelector((appState) => appState.orchestrator.products),
        variants: useSelector((appState) => appState.orchestrator.variants),
        productCategories: useSelector((appState) => appState.orchestrator.productCategories),
        agreements: useSelector((appState) => appState.orchestrator.agreements),
        surveys: useSelector((appState) => appState.orchestrator.surveys),
        conditions: useSelector((appState) => appState.orchestrator.conditions),
        tests: useSelector((appState) => appState.orchestrator.tests),
        vaccinations: useSelector((appState) => appState.orchestrator.vaccinations),
        metrics: useSelector((appState) => appState.orchestrator.metrics),
        shippingClasses: useSelector((appState) => appState.orchestrator.shippingClasses),
        shippingMethods: useSelector((appState) => appState.orchestrator.shippingMethods),
        campaigns: useSelector((appState) => appState.orchestrator.campaigns),
        productFamilies: useSelector((appState) => appState.orchestrator.productFamilies),
        offerGroups: useSelector((appState) => appState.orchestrator.offerGroups),
        commercialEquivalencyGroups: useSelector((appState) => appState.orchestrator.commercialEquivalencyGroups),
        funnelChoiceSets: useSelector((appState) => appState.orchestrator.funnelChoiceSets),
        programs: useSelector((appState) => appState.orchestrator.programs),
        affiliates: useSelector((appState) => appState.orchestrator.affiliates),
        partners: useSelector((appState) => appState.orchestrator.partners),
        articles: useSelector((appState) => appState.orchestrator.articles),
        articleCategories: useSelector((appState) => appState.orchestrator.articleCategories),
        events: useSelector((appState) => appState.orchestrator.events),

        getOrchestrator: (force = false) => dispatch(getOrchestrator(force)),
        getCoveredEntities: (force = false) => dispatch(getCoveredEntities(force)),
        getProviders: (force = false) => dispatch(getProviders(force)),
        getProducts: (force = false) => dispatch(getProducts(force)),
        getVariants: (force = false) => dispatch(getVariants(force)),
        getProductCategories: (force = false) => dispatch(getProductCategories(force)),
        getAgreements: (force = false) => dispatch(getAgreements(force)),
        getSurveys: (force = false) => dispatch(getSurveys(force)),
        getConditions: (force = false) => dispatch(getConditions(force)),
        getTests: (force = false) => dispatch(getTests(force)),
        getVaccinations: (force = false) => dispatch(getVaccinations(force)),
        getMetrics: (force = false) => dispatch(getMetrics(force)),
        getShippingClasses: (force = false) => dispatch(getShippingClasses(force)),
        getShippingMethods: (force = false) => dispatch(getShippingMethods(force)),
        getCampaigns: (force = false) => dispatch(getCampaigns(force)),
        getProductFamilies: (force = false) => dispatch(getProductFamilies(force)),
        getOfferGroups: (force = false) => dispatch(getOfferGroups(force)),
        getCommercialEquivalencyGroups: (force = false) => dispatch(getCommercialEquivalencyGroups(force)),
        getFunnelChoiceSets: (force = false) => dispatch(getFunnelChoiceSets(force)),
        getPrograms: (force = false) => dispatch(getPrograms(force)),
        getAffiliates: (force = false) => dispatch(getAffiliates(force)),
        getPartners: (force = false) => dispatch(getPartners(force)),
        getArticles: (force = false) => dispatch(getArticles(force)),
        getArticleCategories: (force = false) => dispatch(getArticleCategories(force)),
        getEventsInRange: (force = false, startDate, endDate) => dispatch(getEventsInRange(force, startDate, endDate)),

        selectCoveredEntityById,
        selectCoveredEntityBySlug,
        selectProductBySku,
        selectProductByShortSku,
        selectProductBySlug,
        selectAgreementByKey,
        selectSurveyById,
        selectSurveyBySlug,
        selectSurveysByProductSku,
        selectSurveysByCampaignId,
        selectSurveysByCampaignSlug,
        selectSurveysByProgramId,
        selectSurveysByProgramSlug,
        selectCondition,
        selectTest,
        selectVaccination,
        selectMetric,
        selectCampaignById,
        selectCampaignBySlug,
        selectCampaignByProgramSlug,
        selectDefaultCampaignByProgramSlug,
        selectDefaultProductByProgramSlug,
        selectProductsByProgramId,
        selectProductsByProgramSlug,
        selectProductFamilyById,
        selectOfferGroupById,
        selectOfferGroupByToken,
        selectCommercialEquivalencyGroupById,
        selectFunnelChoiceSetById,

        selectProgramById,
        selectProgramBySlug,

        selectAffiliateById,
        selectPartnerById,

        selectProductCategory,
        selectProductCategoryBySlug,
        selectProductCategoriesBySlugs,
        selectChildProductCategories,
        selectProductsByCategoryId,
        selectProductsByCategorySlug,
        selectProductCategoriesByProductId,

        selectVariantsByProductId,
        selectVariantsByProductSlug,
        selectVariantsByProductSku,
        selectVariantById,
        selectVariantBySlug,
        selectVariantBySku,

        selectProviderByUserId,

        selectArticleById,
        selectArticleBySlug,
        selectArticleCategory,
        selectArticleCategoryBySlug,
        selectChildArticleCategories,
        selectArticlesByCategoryId,
        selectArticlesByCategorySlug,
        selectArticleCategoryByArticleId,

        selectEventById,
    };
};

export default orchestratorReducer;
